import { useCallback, useEffect, useState } from 'react';

import { WidgetExternalApplicationType } from '../../../../types/widget';
import { useWidgetImages } from '../../../settings/widgets/provider/WidgetImagesProvider';
import ExternalApplicationItem from '../components/ExternalApplicationItem';
import ExternalApplicationForm from '../configuration/ExternalApplicationForm';
import { ExternalApplicationDetails, ExternalApplicationType } from '../types/externalApplication';
import { FormFieldEnum, SpecificFieldsType } from '../types/form';

type GenericSpecificType = {
	name: string;
	url: string;
	image?: string;
};

type GenericFormProps = {
	application: WidgetExternalApplicationType;
	isNew: boolean;
	onSubmit: (application: WidgetExternalApplicationType) => void;
	onRemove: (application: WidgetExternalApplicationType) => void;
};

export const GenericForm = ({ application, isNew, onSubmit, onRemove }: GenericFormProps) => {
	const { addImages, getBlob, removeImages } = useWidgetImages();

	const { key, specific } = application;
	const jsonSpecific = specific ? (JSON.parse(specific) as GenericSpecificType) : undefined;
	const { url: initialUrl, image: initialImageFileName } = jsonSpecific ?? {};

	const [imageFileName, setImageFileName] = useState<string>();
	const [blob, setBlob] = useState<Blob>();

	const handleImageChange = useCallback(
		(file: File) => {
			const [extension] = file.name.split('.').slice(-1);
			const fileName = `${key}.${extension}`;

			setBlob(file);
			setImageFileName(fileName);
		},
		[key],
	);

	const handleRemove = useCallback(() => {
		imageFileName && removeImages([imageFileName]);

		onRemove(application);
	}, [application, imageFileName, onRemove, removeImages]);

	const handleSubmitForm = useCallback(
		(submittedApplication: WidgetExternalApplicationType) => {
			if (imageFileName && blob) {
				addImages([{ fileName: imageFileName, blob }]);
			}

			onSubmit(submittedApplication);
		},
		[addImages, blob, imageFileName, onSubmit],
	);

	const specificFields: SpecificFieldsType[] = [
		{
			key,
			name: 'url',
			type: FormFieldEnum.TEXTFIELD,
			value: initialUrl,
			description: "URL de l'application.",
			label: 'URL',
			rules: {
				required: true,
				pattern: {
					// Regex explanation
					// ^ : start of string
					// (?:https?:\/\/)? : optional https:// or http://
					// (?:[\w-]+\.?)+ : website domain
					// (?::\d{1,5})? : optional port number simplified to a 1 to 5 digit check (port number should be between 0 and 65535 but the regex would be way longer)
					// (\/?([\w-]+) : optional ending / or subroutes
					// (\?(([\w]+)=(.+)&?)+)?)* : optional parameters
					// $ : end of string
					value: /^(?:https?:\/\/)?(?:[\w-]+\.?)+(?::\d{1,5})?(\/?([\w-]+)(\?(([\w]+)=(.+)&?)+)?)*$/,
					message: 'URL invalide',
				},
			},
		},
		{
			key,
			name: 'image',
			type: FormFieldEnum.IMAGE,
			onChange: handleImageChange,
			value: blob && imageFileName ? new File([blob], imageFileName) : undefined,
			description: "Logo de l'application. La résolution optimale est 96x96.",
			label: 'Logo',
		},
	];

	useEffect(() => {
		if (!initialImageFileName) return;

		(async () => {
			setBlob(await getBlob(initialImageFileName));
			setImageFileName(initialImageFileName);
		})().catch((e) => {
			throw e;
		});
	}, [getBlob, initialImageFileName]);

	return (
		<ExternalApplicationForm
			application={application}
			isNew={isNew}
			specificFields={specificFields}
			onSubmit={handleSubmitForm}
			onRemove={handleRemove}
		/>
	);
};

const Generic = ({ name, specific }: ExternalApplicationType) => {
	const { getBlob } = useWidgetImages();

	const jsonSpecific = specific ? (JSON.parse(specific) as GenericSpecificType) : undefined;
	const { image: imageFileName } = jsonSpecific || {};

	const [imageObjectUrl, setImageObjectUrl] = useState<string>();

	useEffect(() => {
		if (!imageFileName || imageObjectUrl) return undefined;

		(async () => {
			const blob = await getBlob(imageFileName);
			const objectUrl = URL.createObjectURL(blob);
			setImageObjectUrl(objectUrl);
		})().catch((e) => {
			throw e;
		});

		return () => {
			imageObjectUrl && URL.revokeObjectURL(imageObjectUrl);
		};
	}, [getBlob, imageFileName, imageObjectUrl]);

	return <ExternalApplicationItem name={name} imageSrc={imageObjectUrl} />;
};

export const getDetails = ({
	name,
	specific,
}: Pick<ExternalApplicationType, 'name' | 'specific'>): ExternalApplicationDetails => {
	const jsonSpecific = specific ? (JSON.parse(specific) as GenericSpecificType) : undefined;
	const { url } = jsonSpecific || {};

	if (!url) {
		throw new Error('The app url is missing.');
	}

	return {
		name,
		url: /^https?:\/\//.test(url) ? url : `http://${url}`,
		component: <Generic name={name} specific={specific} />,
	};
};
